import { useEffect, useMemo, useRef, useState } from "react";
import { IS_DEV } from "../utils/telegram";
import { useGameStore } from "../store/useGameStore";
import { useBattleStore } from "../store/useBattleStore";
import { useCampaignRunStore } from "../store/useCampaignRunStore";
import type { Battle, BattleLogEvent, BattleUnitInit, Hero } from "../types";

interface DebugRow {
  key: string;
  value: string;
  ok: boolean;
}

function getDebugInfo(): DebugRow[] {
  const rows: DebugRow[] = [];

  const hasTelegram = !!window.Telegram;
  rows.push({ key: "window.Telegram", value: String(hasTelegram), ok: hasTelegram });

  const hasWebApp = !!window.Telegram?.WebApp;
  rows.push({ key: "window.Telegram.WebApp", value: String(hasWebApp), ok: hasWebApp });

  const version = window.Telegram?.WebApp?.version ?? "—";
  rows.push({ key: "WebApp.version", value: version, ok: version !== "—" });

  const platform = window.Telegram?.WebApp?.platform ?? "—";
  rows.push({ key: "WebApp.platform", value: platform, ok: platform !== "—" && platform !== "unknown" });

  const initData = window.Telegram?.WebApp?.initData ?? "";
  rows.push({
    key: "initData",
    value: initData ? `✓ ${initData.length} chars` : "EMPTY",
    ok: initData.length > 0,
  });

  const unsafe = window.Telegram?.WebApp?.initDataUnsafe;
  const userId = unsafe?.user?.id;
  rows.push({
    key: "user.id",
    value: userId ? String(userId) : "—",
    ok: !!userId,
  });

  const username = unsafe?.user?.username ?? unsafe?.user?.first_name ?? "—";
  rows.push({ key: "user.name", value: username, ok: username !== "—" });

  const isExpanded = window.Telegram?.WebApp?.isExpanded;
  rows.push({
    key: "isExpanded",
    value: isExpanded === undefined ? "—" : String(isExpanded),
    ok: !!isExpanded,
  });

  rows.push({
    key: "VITE_API_URL",
    value: import.meta.env.VITE_API_URL ?? "(default /api/v1)",
    ok: true,
  });

  rows.push({
    key: "protocol",
    value: window.location.protocol,
    ok: window.location.protocol === "https:",
  });

  return rows;
}

function fmtDamageLine(dmg: {
  damage?: number;
  skill_damage?: number;
  pre_defense_raw?: number;
  defense_mitigated?: number;
  defense_type?: string;
  defense_stat?: number;
  damage_type?: string;
}): string {
  const dealt = dmg.damage ?? 0;
  const hit = dmg.skill_damage ?? dmg.pre_defense_raw;
  const stat = dmg.defense_stat;
  const dtype = dmg.defense_type ?? (dmg.damage_type === "magical" ? "mdef" : "pdef");
  const blocked = dmg.defense_mitigated ?? (hit != null ? Math.max(0, hit - dealt) : 0);
  if (hit != null && stat != null) {
    return `hit:${hit}  ${dtype}:${stat}  blocked:${blocked}  dmg:${dealt}`;
  }
  if (hit != null) {
    return `hit:${hit}  dmg:${dealt}`;
  }
  return `dmg:${dealt}`;
}

function buildInitRoster(log: BattleLogEvent[]): Map<string, BattleUnitInit> {
  const roster = new Map<string, BattleUnitInit>();
  for (const ev of log) {
    if ((ev.action === "INIT" || ev.action === "JOIN") && ev.units) {
      for (const u of ev.units) roster.set(u.uid, u);
    }
  }
  return roster;
}

function fmtUnitRef(uid: string, roster: Map<string, BattleUnitInit>): string {
  const short = uid.replace("player_", "hero_");
  const u = roster.get(uid);
  if (!u) return short;
  const code = u.monster_code ? ` code:${u.monster_code}` : "";
  return `${short}(${u.name}${code})`;
}

function fmtEvent(ev: BattleLogEvent, roster: Map<string, BattleUnitInit>): string {
  const actor = fmtUnitRef(ev.actor, roster);
  const target = ev.target ? fmtUnitRef(ev.target, roster) : "—";
  const t = String(ev.turn).padStart(2, "0");
  const skill = ev.skill.padEnd(7);

  if (ev.action === "INIT") {
    const roster = (ev.units ?? [])
      .map((u) => {
        const isMob = u.team === 1 && u.atk != null && u.def_ != null;
        const stats = isMob
          ? `atk:${u.atk} def:${u.def_}`
          : `${u.patk > 0 ? `patk:${u.patk}` : `matk:${u.matk}`} pdef:${u.pdef} mdef:${u.mdef}`;
        const extra =
          u.team === 0 && u.crit != null
            ? `crit:${u.crit}`
            : u.team === 1 && u.spd != null
              ? `spd:${u.spd}`
              : "";
        return `${u.uid.replace("player_", "hero_")}(${u.name}${u.monster_code ? ` code:${u.monster_code}` : ""} hp:${u.max_hp} ${stats}${extra ? ` ${extra}` : ""})`;
      })
      .join(", ");
    return `INIT roster: ${roster}`;
  }
  if (ev.action === "ATTACK") {
    const remain = ev.target_hp !== undefined ? ` → hp:${ev.target_hp}` : "";
    if (ev.multi_hit && ev.hits?.length) {
      const hits = ev.hits
        .map((h) => {
          const tgt = fmtUnitRef(h.target, roster);
          return `${tgt} ${fmtDamageLine(h)}(hp:${h.hp})`;
        })
        .join(", ");
      return `T${t} ${actor} [${skill}] multi → ${hits}`;
    }
    return `T${t} ${actor} [${skill}] → ${target}  ${fmtDamageLine(ev)}${remain}`;
  }
  if (ev.action === "AOE") {
    const hits = (ev.hits ?? [])
      .map((h) => {
        const tgt = fmtUnitRef(h.target, roster);
        return `${tgt} ${fmtDamageLine(h)}(hp:${h.hp})`;
      })
      .join(", ");
    return `T${t} ${actor} [${skill}] AOE → ${hits}`;
  }
  if (ev.action === "DOT_TICK") {
    const remain = ev.target_hp !== undefined ? ` → hp:${ev.target_hp}` : "";
    return `T${t} ${actor} [${skill}] DOT → ${target}  ${fmtDamageLine(ev)}${remain}`;
  }
  if (ev.action === "HEAL") {
    return `T${t} ${actor} [${skill}] HEAL +${ev.value ?? 0}`;
  }
  return `T${t} ${actor} [${skill}] ${ev.action}`;
}

function buildBattleText(
  battle: Battle,
  hero: Hero | null,
  visibleLog: BattleLogEvent[],
  phase: string,
): string {
  const lines: string[] = [];
  lines.push(`=== BATTLE #${battle.id} | ${battle.battle_type} | seed:${battle.seed} ===`);
  lines.push(`phase: ${phase}`);
  lines.push("");

  if (hero) {
    lines.push("[HERO]");
    lines.push(
      `  ${hero.hero_class} lv.${hero.level}  hp:${hero.stats.hp}  patk:${hero.stats.patk}  ` +
        `matk:${hero.stats.matk}  pdef:${hero.stats.pdef}  mdef:${hero.stats.mdef}  crit:${hero.stats.crit}`,
    );
    lines.push(`  rotation: ${hero.rotation.join(" → ")}`);
    lines.push("");
  }

  const initEvent = visibleLog.find((ev) => ev.action === "INIT");
  if (initEvent?.units) {
    const enemies = initEvent.units.filter((u) => u.team === 1);
    lines.push(`[ENEMIES] total: ${enemies.length}`);
    for (const u of enemies) {
      const stats =
        u.atk != null && u.def_ != null
          ? `atk:${u.atk}  def:${u.def_}`
          : `${u.patk > 0 ? `patk:${u.patk}` : `matk:${u.matk}`}  pdef:${u.pdef}  mdef:${u.mdef}`;
      lines.push(
        `  ${u.uid}  ${u.name}${u.monster_code ? `  code:${u.monster_code}` : ""}  hp:${u.max_hp}  ${stats}  spd:${u.spd ?? "—"}`,
      );
    }
    lines.push("");
  }

  lines.push(`[BATTLE LOG] ${visibleLog.length} events`);
  const roster = buildInitRoster(visibleLog);
  for (const ev of visibleLog) {
    lines.push("  " + fmtEvent(ev, roster));
  }
  lines.push("");

  if (battle.result) {
    lines.push(`[RESULT] ${battle.result}`);
    if (battle.rewards) {
      lines.push(`  gold:${battle.rewards.gold}  xp:${battle.rewards.xp}  diamonds:${battle.rewards.diamonds}`);
    }
  }

  return lines.join("\n");
}

function buildCampaignBattleText(
  result: NonNullable<ReturnType<typeof useCampaignRunStore.getState>["nodeBattleResult"]>,
  hero: Hero | null,
  visibleLog: BattleLogEvent[],
  done: boolean,
): string {
  const lines: string[] = [];
  lines.push(`=== CAMPAIGN NODE | run:${result.run_id} | node:${result.node_id} ===`);
  lines.push(`phase: ${done ? "results" : "animating"}`);
  lines.push(`display: ${result.display_type}${result.revealed_type ? ` (revealed: ${result.revealed_type})` : ""}`);
  lines.push("");

  if (hero) {
    lines.push("[HERO]");
    lines.push(
      `  ${hero.hero_class} lv.${hero.level}  hp:${hero.stats.hp}  patk:${hero.stats.patk}  ` +
        `matk:${hero.stats.matk}  pdef:${hero.stats.pdef}  mdef:${hero.stats.mdef}  crit:${hero.stats.crit}`,
    );
    lines.push("");
  }

  const initEvent = visibleLog.find((ev) => ev.action === "INIT");
  if (initEvent?.units) {
    const enemies = initEvent.units.filter((u) => u.team === 1);
    lines.push(`[ENEMIES] total: ${enemies.length}`);
    for (const u of enemies) {
      const stats =
        u.atk != null && u.def_ != null
          ? `atk:${u.atk}  def:${u.def_}`
          : `${u.patk > 0 ? `patk:${u.patk}` : `matk:${u.matk}`}  pdef:${u.pdef}  mdef:${u.mdef}`;
      lines.push(
        `  ${u.uid}  ${u.name}${u.monster_code ? `  code:${u.monster_code}` : ""}  hp:${u.max_hp}  ${stats}  spd:${u.spd ?? "—"}`,
      );
    }
    lines.push("");
  }

  lines.push(`[BATTLE LOG] ${visibleLog.length} events`);
  const roster = buildInitRoster(visibleLog);
  for (const ev of visibleLog) {
    lines.push("  " + fmtEvent(ev, roster));
  }
  lines.push("");

  if (result.battle_result) {
    lines.push(`[RESULT] ${result.battle_result}`);
    if (result.rewards) {
      lines.push(`  gold:${result.rewards.gold}  xp:${result.rewards.xp}`);
    }
  }

  return lines.join("\n");
}

export function DebugScreen() {
  const [apiStatus, setApiStatus] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { error: storeError, init, isLoading, hero, setScreen } = useGameStore();
  const { battle, phase, visibleLog } = useBattleStore();
  const { nodeBattleResult, debugVisibleLog, debugReplayDone } = useCampaignRunStore();
  const logBoxRef = useRef<HTMLDivElement>(null);

  const campaignActive = !!nodeBattleResult?.log?.length;
  const activeVisibleLog = campaignActive ? debugVisibleLog : visibleLog;
  const logRoster = useMemo(() => buildInitRoster(activeVisibleLog), [activeVisibleLog]);

  useEffect(() => {
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [activeVisibleLog.length]);

  const battleText = battle
    ? buildBattleText(battle, hero, visibleLog, phase)
    : campaignActive && nodeBattleResult
      ? buildCampaignBattleText(nodeBattleResult, hero, debugVisibleLog, debugReplayDone)
      : null;

  const copyBattleLog = () => {
    if (!battleText) return;
    navigator.clipboard.writeText(battleText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const testApi = async () => {
    setApiStatus("⏳ requesting...");
    const apiUrl = import.meta.env.VITE_API_URL ?? "/api/v1";
    const base = apiUrl.replace(/\/api\/v1\/?$/, "");
    try {
      const res = await fetch(`${base}/health`);
      const data = await res.json().catch(() => ({}));
      setApiStatus(`✅ ${res.status} ${JSON.stringify(data)}`);
    } catch (e) {
      setApiStatus(`❌ ${e instanceof Error ? e.message : "fetch failed"}`);
    }
  };

  return (
    <div className="p-4 pb-8 space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setScreen("admin")}
          className="text-gray-400 hover:text-white border border-kingdom-mid px-3 py-1.5 rounded-lg text-sm"
        >
          ← Admin
        </button>
        <h1 className="text-xl font-bold text-white">🐛 Debug Console</h1>
      </div>

      {storeError && (
        <div className="bg-rose-950 border border-rose-600 rounded-xl p-3">
          <div className="text-rose-400 text-xs font-bold mb-1">App error</div>
          <div className="text-rose-300 text-xs font-mono break-all">{storeError}</div>
        </div>
      )}

      <div className="space-y-1">
        {getDebugInfo().map((row) => (
          <div
            key={row.key}
            className={`rounded-lg px-3 py-2 flex gap-2 text-xs font-mono ${
              row.ok ? "bg-gray-900 text-gray-300" : "bg-rose-950 border border-rose-800 text-rose-300"
            }`}
          >
            <span>{row.ok ? "✓" : "✗"}</span>
            <span className="text-gray-500 shrink-0 w-32">{row.key}</span>
            <span className="break-all">{row.value}</span>
          </div>
        ))}
      </div>

      <div className="bg-gray-900 rounded-xl p-3 space-y-2">
        <button
          onClick={testApi}
          className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg"
        >
          Check backend /health
        </button>
        {apiStatus && <div className="text-xs font-mono text-gray-300 break-all">{apiStatus}</div>}
      </div>

      {battleText && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-xs font-bold uppercase">
              {battle
                ? `Battle #${battle.id} — ${phase}`
                : `Campaign node — ${debugReplayDone ? "results" : "animating"}`}
            </span>
            <button
              onClick={copyBattleLog}
              className="text-[10px] px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-300"
            >
              {copied ? "Copied" : "Copy log"}
            </button>
          </div>
          <div
            ref={logBoxRef}
            className="bg-black rounded-xl p-3 text-[10px] font-mono text-gray-400 overflow-y-auto max-h-60"
          >
            {activeVisibleLog.map((ev, i) => (
              <div key={i} className={ev.actor.startsWith("player") ? "text-emerald-300" : "text-rose-300"}>
                {fmtEvent(ev, logRoster)}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => { void init(); }}
          disabled={isLoading}
          className="flex-1 py-2.5 bg-blue-800 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-bold rounded-xl"
        >
          {isLoading ? "..." : "Retry auth"}
        </button>
        {IS_DEV && (
          <button
            onClick={() => { void init(); }}
            className="flex-1 py-2.5 bg-purple-800 hover:bg-purple-700 text-white text-sm font-bold rounded-xl"
          >
            Dev login
          </button>
        )}
      </div>
    </div>
  );
}
